import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Calendar from 'components/calendar';
import { useDailyTimetableQuery } from 'redux/api_slice/service';
import { Annotation } from './annotation';
import AvailableOptions from './available_options';
import {
  formatQtDate,
  formatWeekdayDateMini,
  getDurationStore,
  getStopInfoFromStops,
  getTimeTableData,
  getUniqTripArray,
} from './helpers';
import StopsInLine from './stops_in_line';
import TableContent from './table_content';
import { useAvailableOptions } from './use_available_options';

const TimetableMultiStops = ({
  duration,
  disableSeasonalities,
  hiddenSeasonalityRouteIds,
}) => {
  const routes = useSelector((state) => state.routes);
  const [availableOptions, selectedOption, setSelectedOption] =
    useAvailableOptions();

  const targetRoute = routes[selectedOption];
  const { route_stops: routeStops } = targetRoute;
  const stopIdsInOrder = useMemo(
    () => routeStops.map((stop) => stop.id),
    [routeStops]
  );

  const [activeStop, setActiveStop] = useState(routeStops[0].name);
  useEffect(() => {
    setActiveStop(routeStops[0].name);
  }, [routeStops]);

  const [durationStore, setDurationStore] = useState({});
  const stopsInfo = useMemo(
    () => getStopInfoFromStops({ routeStops, durationStore }),
    [routeStops, durationStore]
  );

  const [selectedDate, setSelectedDate] = useState(formatQtDate(new Date()));

  const { data: rawData, isFetching } = useDailyTimetableQuery({
    route_id: selectedOption,
    start_date: selectedDate,
    end_date: selectedDate,
  });
  const rawDataOfDay = rawData?.[selectedDate];

  const data = useMemo(() => {
    const uniqTripArray = getUniqTripArray({
      rawData: rawDataOfDay,
      stopIdsInOrder,
    });

    return getTimeTableData({ uniqTripArray, stopIdsInOrder });
  }, [rawDataOfDay, stopIdsInOrder]);

  const columns = stopsInfo.map((stop) => stop.name);

  useEffect(() => {
    const temp = getDurationStore({ rawData: rawDataOfDay, stopIdsInOrder });
    setDurationStore(temp);
  }, [rawDataOfDay, stopIdsInOrder]);

  disableSeasonalities =
    disableSeasonalities || hiddenSeasonalityRouteIds.includes(selectedOption);

  return (
    <div>
      <AvailableOptions
        options={availableOptions}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />
      <StopsInLine
        stops={stopsInfo}
        activeStop={activeStop}
        setActiveStop={setActiveStop}
      />
      <div className="d-flex justify-content-between align-items-sm-center py-4 flex-sm-row flex-column-reverse">
        <div className="position-relative flex-grow-1 flex-basis-0 pt-3 pt-sm-0">
          <label htmlFor="date-select" className="text-primary" role="button">
            <i className="far fa-calendar-alt pe-1" />
            {formatWeekdayDateMini(new Date(selectedDate))}
          </label>
          <Calendar
            inputProps={{
              id: 'date-select',
              className: 'visually-hidden start-0 bottom-0',
              placeholder: 'Please select a date',
            }}
            onChange={(event) => {
              setSelectedDate(formatQtDate(event.value));
            }}
          />
        </div>
        <div className="text-center">
          {duration && (
            <>
              Approximate trip duration
              <h5 className="text-primary">{duration}</h5>
            </>
          )}
        </div>
        <div className="flex-grow-1 flex-basis-0" />
      </div>
      <TableContent
        isFetching={isFetching}
        disableSeasonalities={disableSeasonalities}
        columns={columns}
        activeColumn={activeStop}
        data={data}
      />
      <Annotation />
    </div>
  );
};

TimetableMultiStops.propTypes = {
  duration: PropTypes.string,
  disableSeasonalities: PropTypes.bool,
  hiddenSeasonalityRouteIds: PropTypes.arrayOf(PropTypes.number),
};

export default TimetableMultiStops;
